/**
 * Load the user's organisations and settle on one.
 *
 * **Resolving and remembering are one act.** The hook does not merely return a
 * selection — it calls `setOrgId`, so the fetcher, the permission read and
 * every screen see the same org without anyone having to remember to publish
 * it. Splitting those two steps is what left this app with a `setOrgId` that
 * nothing called and a user staring at "Choose an organisation" with no
 * switcher.
 *
 * It distinguishes three outcomes that look alike and are not:
 *
 * - **loading** — we do not know yet.
 * - **failed** — org-service did not answer. Telling the user they have no
 *   organisation would be a lie, and would send them to support with the wrong
 *   question.
 * - **resolved with no orgs** — genuinely a member of nothing, which is a real
 *   state for a new account.
 */

import { useCallback, useEffect, useState } from 'react'

import { getOrgId, setOrgId, subscribeOrg } from './api'
import { type OrgRow, fetchOrgs, resolveSelected } from './orgs'
import { session } from './session'

export interface UseOrgs {
  orgs: OrgRow[]
  selected: OrgRow | null
  loading: boolean
  failed: boolean
  reload: () => void
}

export function useOrgs(): UseOrgs {
  const current = session.useSession()
  const [orgs, setOrgs] = useState<OrgRow[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  // Re-render when the selection changes, so switching org actually repaints
  // rather than leaving the old org's data on screen.
  const [selectedId, setSelectedId] = useState<string | null>(getOrgId())
  const [nonce, setNonce] = useState(0)

  useEffect(() => subscribeOrg(() => setSelectedId(getOrgId())), [])

  useEffect(() => {
    let cancelled = false
    if (!current) {
      setOrgs([])
      setLoading(false)
      return
    }
    setLoading(true)
    setFailed(false)

    fetchOrgs().then((rows) => {
      if (cancelled) return
      setOrgs(rows)

      if (rows.length === 0) {
        // Ambiguous on purpose at the source: `fetchOrgs` returns [] both for
        // "no memberships" and for "could not ask". We treat an empty list as
        // a failure **only when a selection was already remembered** — a user
        // who had an org a moment ago has not spontaneously left it, so the
        // service is the likelier explanation.
        setFailed(getOrgId() !== null)
        setLoading(false)
        return
      }

      const chosen = resolveSelected(rows)
      // The act of publishing it. `setOrgId` persists and notifies; without
      // this line the app has organisations and still no org.
      setOrgId(chosen ? chosen.id : null)
      setSelectedId(chosen ? chosen.id : null)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [current?.userId, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  const selected = orgs.find((o) => o.id === selectedId) ?? null
  return { orgs, selected, loading, failed, reload }
}
